'use client';

import { useEffect, useRef, useState } from 'react';
import { formatPrice } from '@/utils/utilities.mjs';
import { useProduct } from '@/contexts/ProductContext';
import { usePricing } from '@/contexts/PricingContext';
import { useModal } from '@/components/ModalContext';
import PriceDetails from '@/components/global/PriceDetails';
import SaveForLaterModal from '@/components/product/SaveForLaterModal';
import { openZaiusCampaign } from '@/components/integrations/Zaius/Modal';
import { setLauncherClearance } from '@/components/integrations/Kodif/launcherClearance';
import InfoIcon from '@/components/icons/InfoIcon';

const STICKY_PRICE_KEY = 'v2-sticky-price';

export default function V2StickyPrice({ saveForLaterBlock }) {
  const [isVisible, setIsVisible] = useState(false);
  const barRef = useRef(null);
  const { selectedVariant, selectedVariantPrices, productData } = useProduct();
  const pricing = usePricing();
  const { openModal } = useModal();

  // Stands in for the buy box price row while it is still below the fold.
  // `top > 0` excludes the scrolled-past case, which also fails isIntersecting.
  // Re-queried per variant: V2BuyBoxCard renders nothing until one resolves.
  useEffect(() => {
    const priceRow = document.querySelector('[data-block="v2_price_row"]');
    if (!priceRow) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(!entry.isIntersecting && entry.boundingClientRect.top > 0);
      },
      { threshold: 0 },
    );

    observer.observe(priceRow);
    return () => observer.disconnect();
  }, [selectedVariant]);

  // BL-924: Kodif's launcher is a fixed iframe with the maximum z-index, so
  // this bar can never be stacked above it. Registering the bar's height moves
  // the launcher above it instead — see KodifLauncherOffset.
  useEffect(() => {
    const bar = barRef.current;

    if (!isVisible || !bar) {
      setLauncherClearance(STICKY_PRICE_KEY, 0);
      return undefined;
    }

    // offsetHeight (border-box) — contentRect drops the bar's own padding,
    // which would under-measure it and leave the launcher overlapping.
    const observer = new ResizeObserver(([entry]) => {
      setLauncherClearance(STICKY_PRICE_KEY, entry.target.offsetHeight);
    });
    observer.observe(bar);

    return () => {
      observer.disconnect();
      setLauncherClearance(STICKY_PRICE_KEY, 0);
    };
  }, [isVisible]);

  if (!selectedVariant) return null;

  const currencyCode = selectedVariant.price?.currencyCode ?? 'USD';

  // `pricing.variant()` returns nulls when settings.pricing.strategy is unset
  // (Pricing.js getCurrentStrategy), which would render $0. Same fallback as
  // ProductDetailsPrice, so both price displays stay in agreement.
  const fallbackPrice = parseFloat(selectedVariant.price?.amount ?? 0);
  const price = selectedVariantPrices?.sale ?? fallbackPrice;
  const fullPrice = selectedVariantPrices?.full ?? fallbackPrice;
  const hasDiscount = selectedVariantPrices?.hasPriceDifference && fullPrice > price;

  const promotionalCopy = pricing.promotionalCopy(selectedVariant, 'compact');
  const saveForLaterLabel = saveForLaterBlock?.title || 'Save For Later';

  return (
    <div
      ref={barRef}
      className={`fixed inset-x-0 bottom-0 z-20 bg-white shadow-[0_-4px_4px_rgba(0,0,0,0.08)] pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] transition-all delay-150 duration-150 ease-in-out font-worksans text-primary ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
      }`}
      data-block="v2_sticky_price"
      role="region"
      aria-label="Product price"
      inert={!isVisible}
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-[auto_23rem] lg:gap-x-4 xl:gap-x-10 items-start">
          <div className="hidden lg:block" />
          <div className="lg:px-2">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-[28px] font-semibold leading-[150%] text-primary">
                    {formatPrice(price, currencyCode)}
                  </span>

                  {hasDiscount && (
                    <span className="text-xl font-light leading-[150%] text-primary line-through">
                      {formatPrice(fullPrice, currencyCode)}
                    </span>
                  )}
                </div>

                {hasDiscount && promotionalCopy && (
                  <button
                    type="button"
                    onClick={openZaiusCampaign}
                    aria-haspopup="dialog"
                    aria-label={`${promotionalCopy} — see campaign details`}
                    className="cursor-pointer bg-warm-white px-4 py-2 text-primary"
                  >
                    <span className="flex items-center justify-center gap-2.5 group">
                      <span className="text-sm leading-[150%] font-normal whitespace-nowrap">{promotionalCopy}</span>
                      <InfoIcon className="size-4" />
                    </span>
                  </button>
                )}
              </div>

              <div className="flex items-center justify-between gap-4">
                <PriceDetails />

                <button
                  type="button"
                  className="flex items-center font-worksans text-sm py-0.5 text-sea cursor-pointer underline justify-end text-right"
                  onClick={() =>
                    openModal(
                      <SaveForLaterModal productData={productData} selectedVariant={selectedVariant} />,
                    )
                  }
                >
                  {saveForLaterLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
